import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-client';

/**
 * API endpoint for AI-generated recommendation rationale.
 * nginx proxies relative /api/* URLs to bankiq-api (nginx.conf §"Reverse proxy").
 */
const RATIONALE_URL = '/api/intelligence/recommendations/rationale';

/**
 * Demo tenant ID shared across all frontend API calls.
 * Must match the seeded tenant in bankiq-postgres (SCRUM-56/68 DB-first path).
 */
// SCRUM-643: tenant from the App ID token claim (SCRUM-102), no hard-coded header.

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FactItem {
  label: string;
  value: string;
}

export interface RationaleRequest {
  productName: string;
  /** Number of ICP (Ideal Customer Profile) criteria the customer matched. */
  icpMatchCount: number;
  /** Supporting context facts; non-PII only. */
  facts: FactItem[];
}

/**
 * Mirrors `AiContentResponse` from the backend (SCRUM-51 endpoint).
 * `aiGenerated: false` + `content: ''` = gateway unavailable; UI shows nothing.
 */
export interface RationaleResult {
  content: string;
  aiGenerated: boolean;
  model: string;
}

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchRationale(req: RationaleRequest): Promise<RationaleResult> {
  const res = await apiFetch(RATIONALE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      productName: req.productName,
      icpMatchCount: req.icpMatchCount,
      facts: req.facts,
    }),
  });

  if (!res.ok) {
    throw new Error(`POST ${RATIONALE_URL} failed: ${res.status.toString()} ${res.statusText}`);
  }

  const data = (await res.json()) as RationaleResult;

  // Treat an empty/unavailable fallback as a retryable transient failure.
  // The backend returns {content:'', aiGenerated:false} on cold-start or gateway
  // unavailability. A 200-with-empty-body is NOT a React Query error by default,
  // so we throw manually to trigger retry and prevent 5-min caching of the blank state.
  // SCRUM-236: belt-and-suspenders UI hardening alongside Min Scale 1 infra fix.
  if (!data.aiGenerated) {
    throw new Error('AI gateway unavailable (aiGenerated:false) — will retry');
  }

  return data;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Fetches an AI-generated recommendation rationale from the Intelligence API.
 *
 * Returns `{ data, isLoading, isError }` from React Query.
 * - `data.aiGenerated === true`  → show `data.content` + AI badge
 * - `isError === true`           → gateway unavailable or network failure after all retries;
 *                                   caller shows fallback (no rationale text, no badge)
 *
 * Results are cached 5 minutes per unique `(productName, icpMatchCount, facts)`
 * combination — the fetcher only resolves when `aiGenerated:true`, so the cache
 * never holds a blank/fallback result.
 *
 * On a cold-start or transient gateway unavailability, the fetcher throws and
 * React Query retries up to 2 times with exponential backoff (1s → 2s, capped 4s).
 * If all retries fail the UI falls back silently — rationale is an enhancement,
 * not core functionality. SCRUM-236: belt-and-suspenders alongside Min Scale 1 fix.
 *
 * `throwOnError: false` keeps failures non-fatal (captured in `isError`, not thrown
 * to React error boundaries).
 *
 * Implemented under SCRUM-51 (Recommendation Rationale frontend wiring).
 * Self-healing retry added under SCRUM-236 (cold-start blank rationale fix).
 */
export function useRationale(req: RationaleRequest) {
  // Build a stable cache key from all request parameters.
  const factsKey = req.facts.map((f) => `${f.label}:${f.value}`).join('|');

  return useQuery<RationaleResult>({
    queryKey: ['rationale', req.productName, req.icpMatchCount, factsKey],
    queryFn: () => fetchRationale(req),
    staleTime: 5 * 60 * 1000, // 5 min — only reached on aiGenerated:true (fetcher throws otherwise)
    // SCRUM-253: thin backstop for a genuine first-ever generation of a pairing.
    // The durable fix is the server-side cache (Layer A) + min-scale=1 — those make
    // repeat views instant and cold starts rare — so this only needs to cover a
    // single cold first-view: [1.5s, 3s, 5s] ≈ 9.5s across 4 attempts.
    retry: 3,
    retryDelay: (failureCount) => Math.min(1500 * 2 ** failureCount, 5000),
    throwOnError: false,
  });
}
